import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Box, IconButton, Typography, } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
const ServiceHistoryModal = ({ open, serviceHistory, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        serviceDate: '',
        hours: undefined,
        nextServiceDate: '',
        description: '',
        partsUsed: [],
        cost: undefined,
        performedBy: '',
        notes: '',
    });
    const [errors, setErrors] = useState({});
    useEffect(() => {
        if (serviceHistory) {
            setFormData({
                serviceDate: serviceHistory.serviceDate.split('T')[0],
                hours: serviceHistory.hours || undefined,
                nextServiceDate: serviceHistory.nextServiceDate ? serviceHistory.nextServiceDate.split('T')[0] : '',
                description: serviceHistory.description || '',
                partsUsed: serviceHistory.partsUsed || [],
                cost: serviceHistory.cost || undefined,
                performedBy: serviceHistory.performedBy || '',
                notes: serviceHistory.notes || '',
            });
        }
        else {
            setFormData({
                serviceDate: new Date().toISOString().split('T')[0],
                hours: undefined,
                nextServiceDate: '',
                description: '',
                partsUsed: [],
                cost: undefined,
                performedBy: '',
                notes: '',
            });
        }
        setErrors({});
    }, [serviceHistory, open]);
    const handleChange = (e) => {
        const { name, value } = e.target;
        // Handle cost and hours as number
        if (name === 'cost' || name === 'hours') {
            const numValue = value === '' ? undefined : parseFloat(value);
            setFormData((prev) => (Object.assign(Object.assign({}, prev), { [name]: numValue })));
        }
        else {
            setFormData((prev) => (Object.assign(Object.assign({}, prev), { [name]: value })));
        }
        // Clear error for this field
        if (errors[name]) {
            setErrors((prev) => (Object.assign(Object.assign({}, prev), { [name]: '' })));
        }
    };
    const handleAddPart = () => {
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { partsUsed: [...(prev.partsUsed || []), { itemName: '', itemsUsed: 1 }] })));
    };
    const handleRemovePart = (index) => {
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { partsUsed: (prev.partsUsed || []).filter((_, i) => i !== index) })));
    };
    const handlePartChange = (index, field, value) => {
        setFormData((prev) => {
            const newParts = [...(prev.partsUsed || [])];
            newParts[index] = Object.assign(Object.assign({}, newParts[index]), { [field]: field === 'itemsUsed' ? (value === '' ? 1 : parseInt(value, 10)) : value });
            return Object.assign(Object.assign({}, prev), { partsUsed: newParts });
        });
    };
    const validate = () => {
        var _a;
        const newErrors = {};
        if (!formData.serviceDate) {
            newErrors.serviceDate = 'Service date is required';
        }
        if (!((_a = formData.description) === null || _a === void 0 ? void 0 : _a.trim())) {
            newErrors.description = 'Description is required';
        }
        if (formData.nextServiceDate && formData.serviceDate && formData.nextServiceDate < formData.serviceDate) {
            newErrors.nextServiceDate = 'Next service date must be after service date';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = () => {
        var _a;
        if (validate()) {
            // Clean up the data before sending
            const dataToSend = {
                serviceDate: formData.serviceDate,
                hours: formData.hours || undefined,
                nextServiceDate: formData.nextServiceDate || undefined,
                description: formData.description,
                partsUsed: ((_a = formData.partsUsed) === null || _a === void 0 ? void 0 : _a.filter(p => p.itemName.trim() !== '')) || [],
                cost: formData.cost || undefined,
                performedBy: formData.performedBy || undefined,
                notes: formData.notes || undefined,
            };
            onSave(dataToSend);
        }
    };
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "md", fullWidth: true, children: [_jsx(DialogTitle, { children: serviceHistory ? 'Edit Service Record' : 'Add Service Record' }), _jsx(DialogContent, { children: _jsxs(Grid, { container: true, spacing: 2, sx: { mt: 1 }, children: [_jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsx(TextField, { fullWidth: true, label: "Service Date", name: "serviceDate", type: "date", value: formData.serviceDate, onChange: handleChange, error: !!errors.serviceDate, helperText: errors.serviceDate, required: true, InputLabelProps: { shrink: true } }) }), _jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsx(TextField, { fullWidth: true, label: "Hours", name: "hours", type: "number", value: formData.hours || '', onChange: handleChange, inputProps: { min: 0, step: 0.1 } }) }), _jsx(Grid, { item: true, xs: 12, sm: 6, sx: { display: 'none' }, children: _jsx(TextField, { fullWidth: true, label: "Next Service Date", name: "nextServiceDate", type: "date", value: formData.nextServiceDate, onChange: handleChange, error: !!errors.nextServiceDate, helperText: errors.nextServiceDate, InputLabelProps: { shrink: true } }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, label: "Description", name: "description", value: formData.description, onChange: handleChange, error: !!errors.description, helperText: errors.description, required: true, multiline: true, rows: 2 }) }), _jsxs(Grid, { item: true, xs: 12, sx: { display: 'none' }, children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }, children: [_jsx(Typography, { variant: "subtitle2", children: "Parts Used" }), _jsx(Button, { startIcon: _jsx(AddIcon, {}), size: "small", onClick: handleAddPart, children: "Add Part" })] }), formData.partsUsed && formData.partsUsed.length > 0 ? (formData.partsUsed.map((part, index) => (_jsxs(Box, { sx: { display: 'flex', gap: 1, mb: 1 }, children: [_jsx(TextField, { size: "small", label: "Part Name", value: part.itemName, onChange: (e) => handlePartChange(index, 'itemName', e.target.value), sx: { flex: 2 } }), _jsx(TextField, { size: "small", label: "Quantity", type: "number", value: part.itemsUsed, onChange: (e) => handlePartChange(index, 'itemsUsed', e.target.value), sx: { flex: 1 }, inputProps: { min: 1 } }), _jsx(IconButton, { size: "small", color: "error", onClick: () => handleRemovePart(index), children: _jsx(DeleteIcon, {}) })] }, index)))) : (_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { fontStyle: 'italic' }, children: "No parts added yet" }))] }), _jsx(Grid, { item: true, xs: 12, sm: 6, sx: { display: 'none' }, children: _jsx(TextField, { fullWidth: true, label: "Cost", name: "cost", type: "number", value: formData.cost || '', onChange: handleChange, inputProps: { min: 0, step: 0.01 } }) }), _jsx(Grid, { item: true, xs: 12, sm: 6, sx: { display: 'none' }, children: _jsx(TextField, { fullWidth: true, label: "Performed By", name: "performedBy", value: formData.performedBy, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, sx: { display: 'none' }, children: _jsx(TextField, { fullWidth: true, label: "Notes", name: "notes", value: formData.notes, onChange: handleChange, multiline: true, rows: 3 }) })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: onClose, children: "Cancel" }), _jsx(Button, { onClick: handleSubmit, variant: "contained", children: serviceHistory ? 'Update' : 'Create' })] })] }));
};
export default ServiceHistoryModal;
